import { Swipe } from '../utils/swipe';
import _debounce from 'lodash-es/debounce';

export class ScrollableTabHeader {
    constructor(ulElement, backButton, forwardButton) {
        this.swipe = new Swipe();

        this.clientWidth = 0; // this needs to be updated manually, since the svelte convenience does not exist
        this.scrollWidth = 0;
        this.scrollLeft = 0;
        this.shouldShowScrollHint = false;

        this.startReached = true;
        this.endReached = false;
        this.ul = ulElement;
        this.backButton = backButton;
        this.forwardButton = forwardButton;

        this.onResize = _debounce(this.onResize.bind(this), 200);
        this.onSwipeChanged = _debounce(this.onSwipeChanged.bind(this), 50);
        this.onScroll = _debounce(this.onScroll.bind(this), 50);

        this.onWheel = this.onWheel.bind(this);
        this.scrollToLeft = this.scrollToLeft.bind(this);
        this.scrollToRight = this.scrollToRight.bind(this);

        this.listen();

        this.onResize();

        // rewind on init
        this.ul.scrollLeft = 0;
    }

    /**
     * Attach all the required event listeners.
     */
    listen() {
        this.swipe.attach(this.ul, { onSwipeChanged: this.onSwipeChanged });

        window.addEventListener('resize', this.onResize);

        this.ul.addEventListener('scroll', this.onScroll);
        this.ul.addEventListener('wheel', this.onWheel);

        if (!this.shouldShowScrollHint) {
            this.backButton.classList['add']('hidden');
            this.forwardButton.classList['add']('hidden');
        }

        if (this.backButton) {
            this.backButton.addEventListener('click', this.scrollToLeft);
        }

        if (this.forwardButton) {
            this.forwardButton.addEventListener('click', this.scrollToRight);
        }
    }

    /**
     * Callback for the window object's "resize" event to recalculate all
     * the relevant dimensions.
     */
    onResize() {
        this.scrollWidth = (this.ul && this.ul.scrollWidth) || 0;
        this.scrollLeft = this.ul.scrollLeft;
        this.clientWidth = this.getElementClientWidth(this.ul);
        this.recalculate();
    }

    /**
     * Callback for the "scroll" event to update the ul's scrollLeft value,
     * which calculations are based on.
     *
     * @param {Event} e
     */
    onScroll(e) {
        this.scrollLeft = e.target.scrollLeft;
        this.recalculate();
    }

    /**
     * Callback for the swipe class' swiping events.
     *
     * @param {number} newScrollLeft
     */
    onSwipeChanged({ scrollLeft: newScrollLeft }) {
        this.scrollLeft = newScrollLeft;
        this.recalculate();
    }

    /**
     * Callback for the mousewheel "wheel" event to determine and apply
     * scrolling.
     *
     * @param {WheelEvent} e
     */
    onWheel(e) {
        const delta = e.deltaX * 10 || e.deltaY * 2;
        this.ul.scrollLeft += delta;
    }

    /**
     * Recalculate the scroll positions and determine whether to show the scroll
     * hints left and/or right.
     */
    recalculate() {
        this.maybeStartReached();
        this.maybeEndReached();
        this.maybeShowScrollHint();
    }

    /**
     * Scroll to the left by a fixed amount.
     */
    scrollToLeft() {
        this.ul.scrollLeft -= 200;
    }

    /**
     * Scroll to the right by a fixed amount.
     */
    scrollToRight() {
        this.ul.scrollLeft += 200;
    }

    /**
     * Calculate whether the scroll hints need to be shown and show or hide them
     * as appropriate.
     */
    maybeShowScrollHint() {
        this.shouldShowScrollHint = this.clientWidth < this.scrollWidth;

        if (this.shouldShowScrollHint) {
            const backButtonMethod = this.startReached ? 'add' : 'remove';
            const forwardButtonMethod = this.endReached ? 'add' : 'remove';
            this.backButton.classList[backButtonMethod]('hidden');
            this.forwardButton.classList[forwardButtonMethod]('hidden');
        }
    }

    /**
     * Calculate if scrolling has reached the end of the scroll container.
     */
    maybeEndReached() {
        this.endReached =
            this.scrollLeft + this.clientWidth >= this.scrollWidth;
    }

    /**
     * Calculate if scrolling is at the start of the scroll container.
     */
    maybeStartReached() {
        this.startReached = this.scrollLeft === 0;
    }

    /**
     * Calculate an element's client width via getBoundingClientRect.
     *
     * @param {Element} element
     * @return {number|null}
     */
    getElementClientWidth(element) {
        const rect = element.getBoundingClientRect();
        return rect.width;
    }
}
